// Firebase initialization and configuration
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
  initializeFirestore,
} from 'firebase/firestore';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, Functions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { config } from './config';

// Get Firebase config from environment or config file
function getFirebaseConfig() {
  // Use environment variables from config (already loaded from .env)
  return {
    apiKey: config.firebase.apiKey,
    authDomain: config.firebase.authDomain,
    projectId: config.firebase.projectId,
    storageBucket: config.firebase.storageBucket,
    messagingSenderId: config.firebase.messagingSenderId,
    appId: config.firebase.appId,
    measurementId: config.firebase.measurementId,
  };
}

const firebaseConfig = getFirebaseConfig();

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;

/**
 * Initialize Firebase if not already initialized
 */
export const initializeFirebase = (): FirebaseApp => {
  if (app) {
    return app;
  }

  // Check if Firebase is already initialized
  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
  } else {
    app = initializeApp(firebaseConfig);
  }

  return app;
};

/**
 * Get Firestore database instance
 */
export const getFirestoreDB = (): Firestore => {
  if (!app) {
    initializeFirebase();
  }
  
  if (!db) {
    // Default: force long-polling so restrictive networks don't 404 WebChannel `/Listen/channel`
    // and leave the Live badge stuck on "Connecting…". Opt out with VITE_FIRESTORE_FORCE_LONG_POLLING=false.
    const useLongPollTransport = import.meta.env.VITE_FIRESTORE_FORCE_LONG_POLLING !== 'false';
    if (useLongPollTransport) {
      try {
        db = initializeFirestore(app!, {
          experimentalForceLongPolling: true,
        });
      } catch {
        db = getFirestore(app!);
      }
    } else {
      db = getFirestore(app!);
    }

    // Connect to emulator in development if needed
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATOR === 'true') {
      try {
        connectFirestoreEmulator(db, 'localhost', 8080);
      } catch (e) {
        // Emulator already connected
      }
    }
  }
  
  return db;
};

/**
 * Get Firebase Auth instance
 */
export const getFirebaseAuth = (): Auth => {
  if (!app) {
    initializeFirebase();
  }
  
  if (!auth) {
    auth = getAuth(app!);
    
    // Connect to emulator in development if needed
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATOR === 'true') {
      try {
        connectAuthEmulator(auth, 'http://localhost:9099');
      } catch (e) {
        // Emulator already connected
      }
    }
  }
  
  return auth;
};

/**
 * Get Firebase Storage instance
 */
export const getFirebaseStorage = (): FirebaseStorage => {
  if (!app) {
    initializeFirebase();
  }
  
  if (!storage) {
    storage = getStorage(app!);
    
    // Connect to emulator in development if needed
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATOR === 'true') {
      try {
        connectStorageEmulator(storage, 'localhost', 9199);
      } catch (e) {
        // Emulator already connected
      }
    }
  }
  
  return storage;
};

/**
 * Get Firebase Functions instance
 */
export const getFirebaseFunctions = (): Functions => {
  if (!app) {
    initializeFirebase();
  }

  if (!functions) {
    functions = getFunctions(app!);
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATOR === 'true') {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      } catch (e) {
        // Emulator already connected
      }
    }
  }

  return functions;
};

/**
 * Check if Firebase is enabled
 */
export const isFirebaseEnabled = (): boolean => {
  return !!firebaseConfig.apiKey && 
         !!firebaseConfig.projectId && 
         firebaseConfig.apiKey !== 'YOUR_API_KEY' &&
         firebaseConfig.apiKey !== '';
};

// Initialize on import
if (isFirebaseEnabled()) {
  try {
    initializeFirebase();
  } catch (error: any) {
    // Log as warning instead of error - Firebase might not be available in some environments
    // The app can handle Firebase being unavailable gracefully
    console.warn('⚠️ Firebase initialization issue (app will continue):', error?.message || error);
  }
}

export { app, db, auth, storage, functions };

